import { ThunkAction } from 'redux-thunk';

import { ESearchFilters } from '@/constants/searchFilters';
import { EHeaderSearchFilters } from '@/redux/modules/globalHeader';
import { TRootState } from '@/redux/rootReducer';
import {
  fetchOneWayListings,
  fetchOneWayLocationsList,
  fetchOneWayResultCount,
  IOneWayListing,
  IOneWayLocation,
} from '@/services/oneWaySearchApiRequest';
import { TQueryParams } from '@/services/searchApiRequest';
import { IAction } from '@/utility/redux/action';

const FETCH_LOAD = 'OneWaySearch/FETCH_LOAD';
const FETCH_SUCCESS = 'OneWaySearch/FETCH_SUCCESS';
const FETCH_ERROR = 'OneWaySearch/FETCH_ERROR';
const FETCH_LOCATIONS_LOAD = 'OneWaySearch/FETCH_LOCATIONS_LOAD';
const FETCH_LOCATIONS_SUCCESS = 'OneWaySearch/FETCH_LOCATIONS_SUCCESS';
const FETCH_LOCATIONS_ERROR = 'OneWaySearch/FETCH_LOCATIONS_ERROR';
const FETCH_META_LOAD = 'OneWaySearch/FETCH_META_LOAD';
const FETCH_META_SUCCESS = 'OneWaySearch/FETCH_META_SUCCESS';
const FETCH_META_ERROR = 'OneWaySearch/FETCH_META_ERROR';

interface IFetchLoadAction extends IAction {
  type: typeof FETCH_LOAD;
}

interface IFetchSuccessAction extends IAction {
  type: typeof FETCH_SUCCESS;
  payload: IOneWayListing[];
}

interface IFetchErrorAction extends IAction {
  type: typeof FETCH_ERROR;
  payload: string;
}

interface IFetchLocationsLoadAction extends IAction {
  type: typeof FETCH_LOCATIONS_LOAD;
}

interface IFetchLocationsSuccessAction extends IAction {
  type: typeof FETCH_LOCATIONS_SUCCESS;
  payload: { pickups: IOneWayLocation[]; dropoffs: IOneWayLocation[] };
}

interface IFetchLocationsErrorAction extends IAction {
  type: typeof FETCH_LOCATIONS_ERROR;
  payload: string;
}

interface IFetchMetaLoadAction extends IAction {
  type: typeof FETCH_META_LOAD;
}

interface IFetchMetaSuccessAction extends IAction {
  type: typeof FETCH_META_SUCCESS;
  payload: number;
}

interface IFetchMetaErrorAction extends IAction {
  type: typeof FETCH_META_ERROR;
  payload: string;
}

type TAction =
  | IFetchLoadAction
  | IFetchSuccessAction
  | IFetchErrorAction
  | IFetchLocationsLoadAction
  | IFetchLocationsSuccessAction
  | IFetchLocationsErrorAction
  | IFetchMetaLoadAction
  | IFetchMetaSuccessAction
  | IFetchMetaErrorAction;

const fetchLoad = (): IFetchLoadAction => ({ type: FETCH_LOAD });
const fetchSuccess = (payload: IOneWayListing[]): IFetchSuccessAction => ({
  type: FETCH_SUCCESS,
  payload,
});
const fetchError = (payload: string): IFetchErrorAction => ({
  type: FETCH_ERROR,
  payload,
});

const fetchLocationsLoad = (): IFetchLocationsLoadAction => ({ type: FETCH_LOCATIONS_LOAD });
const fetchLocationsSuccess = (payload: {
  pickups: IOneWayLocation[];
  dropoffs: IOneWayLocation[];
}): IFetchLocationsSuccessAction => ({
  type: FETCH_LOCATIONS_SUCCESS,
  payload,
});
const fetchLocationsError = (payload: string): IFetchLocationsErrorAction => ({
  type: FETCH_LOCATIONS_ERROR,
  payload,
});

const fetchMetaLoad = (): IFetchMetaLoadAction => ({ type: FETCH_META_LOAD });
const fetchMetaSuccess = (payload: number): IFetchMetaSuccessAction => ({
  type: FETCH_META_SUCCESS,
  payload,
});
const fetchMetaError = (payload: string): IFetchMetaErrorAction => ({
  type: FETCH_META_ERROR,
  payload,
});

// Build the API params for /one-way-listings from redux state.
//
// queryParams is the source of truth for filters (driven through
// `applySearchFilter`, which keeps URL + state.queryParams in sync). Guests
// are read from `globalHeader` to match how the SRP search bar stores them.
const buildOneWayQueryParams = (state: TRootState): TQueryParams => {
  const queryParams = state.queryParams;
  const guests = state.globalHeader[EHeaderSearchFilters.GUESTS];

  const params: TQueryParams = {
    'filter[start_city]': queryParams['filter[start_city]'] as string | undefined,
    'filter[start_state]': queryParams['filter[start_state]'] as string | undefined,
    'filter[end_city]': queryParams['filter[end_city]'] as string | undefined,
    'filter[end_state]': queryParams['filter[end_state]'] as string | undefined,
    [ESearchFilters.DATE_FROM]: queryParams[ESearchFilters.DATE_FROM] as string | undefined,
    [ESearchFilters.DATE_TO]: queryParams[ESearchFilters.DATE_TO] as string | undefined,
    [ESearchFilters.FILTER_RENTER_AGE]: queryParams[ESearchFilters.FILTER_RENTER_AGE] as
      | string
      | undefined,
    [ESearchFilters.INSTANT_BOOK]: queryParams[ESearchFilters.INSTANT_BOOK] as string | undefined,
    [ESearchFilters.FILTER_TYPE]: queryParams[ESearchFilters.FILTER_TYPE] as string | undefined,
    [ESearchFilters.FILTER_FEATURE]: queryParams[ESearchFilters.FILTER_FEATURE] as
      | string
      | undefined,
    'sleeps[adults]': guests?.adults,
    'sleeps[children]': guests?.children,
    'sleeps[infants]': guests?.infants,
  };

  Object.keys(params).forEach(key => {
    const value = params[key];
    if (value === undefined || value === null || value === '') {
      delete params[key];
    }
  });

  return params;
};

export const fetchOneWayResults =
  (): ThunkAction<Promise<void>, TRootState, void, TAction> => async (dispatch, getState) => {
    dispatch(fetchLoad());
    try {
      const params = buildOneWayQueryParams(getState());
      const listings = await fetchOneWayListings(params);
      dispatch(fetchSuccess(listings));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load one-way listings.';
      dispatch(fetchError(message));
    }
  };

export const fetchOneWayLocations =
  (): ThunkAction<Promise<void>, TRootState, void, TAction> => async dispatch => {
    dispatch(fetchLocationsLoad());
    try {
      const locations = await fetchOneWayLocationsList();
      dispatch(fetchLocationsSuccess(locations));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load one-way locations.';
      dispatch(fetchLocationsError(message));
    }
  };

// Keys we forward from searchForm.filters (tentative modal state) into the
// meta preview request. Anything not listed here is either irrelevant to
// one-way (price ranges, delivery, campground subtypes, etc.) or already
// covered by queryParams. Array values get joined to CSV, booleans get
// stringified.
const ONE_WAY_FORM_FILTER_KEYS: ESearchFilters[] = [
  ESearchFilters.FILTER_TYPE,
  ESearchFilters.FILTER_FEATURE,
  ESearchFilters.FILTER_RENTER_AGE,
  ESearchFilters.INSTANT_BOOK,
];

const overlayFormFilters = (
  base: TQueryParams,
  formFilters: Record<string, unknown>,
): TQueryParams => {
  const out = { ...base };
  for (const key of ONE_WAY_FORM_FILTER_KEYS) {
    const value = formFilters[key];
    if (value === undefined || value === null || value === '') {
      // Form has an explicit clear → drop the param entirely. (Without this
      // a stale committed queryParams value would survive a modal clear.)
      delete out[key];
      continue;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        delete out[key];
        continue;
      }
      out[key] = value.join(',');
    } else {
      out[key] = String(value);
    }
  }
  return out;
};

export const fetchOneWayMetaResults =
  (): ThunkAction<Promise<void>, TRootState, void, TAction> => async (dispatch, getState) => {
    dispatch(fetchMetaLoad());
    try {
      const state = getState();
      const committed = buildOneWayQueryParams(state);
      const params = overlayFormFilters(
        committed,
        state.searchForm.filters as unknown as Record<string, unknown>,
      );
      const total = await fetchOneWayResultCount(params);
      dispatch(fetchMetaSuccess(total));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to preview one-way results.';
      dispatch(fetchMetaError(message));
    }
  };

interface IState {
  listings: IOneWayListing[];
  loading: boolean;
  error: string | null;
  locations: {
    pickups: IOneWayLocation[];
    dropoffs: IOneWayLocation[];
  };
  locationsLoading: boolean;
  locationsError: string | null;
  meta: {
    total: number | null;
    loading: boolean;
    error: string | null;
  };
}

export const initialState: IState = {
  listings: [],
  loading: false,
  error: null,
  locations: { pickups: [], dropoffs: [] },
  locationsLoading: false,
  locationsError: null,
  meta: { total: null, loading: false, error: null },
};

export default function reducer(state = initialState, action: TAction): IState {
  switch (action.type) {
    case FETCH_LOAD:
      return { ...state, loading: true, error: null };
    case FETCH_SUCCESS:
      return { ...state, loading: false, listings: action.payload, error: null };
    case FETCH_ERROR:
      return { ...state, loading: false, error: action.payload };
    case FETCH_LOCATIONS_LOAD:
      return { ...state, locationsLoading: true, locationsError: null };
    case FETCH_LOCATIONS_SUCCESS:
      return {
        ...state,
        locationsLoading: false,
        locations: action.payload,
        locationsError: null,
      };
    case FETCH_LOCATIONS_ERROR:
      return { ...state, locationsLoading: false, locationsError: action.payload };
    case FETCH_META_LOAD:
      return { ...state, meta: { ...state.meta, loading: true, error: null } };
    case FETCH_META_SUCCESS:
      return { ...state, meta: { total: action.payload, loading: false, error: null } };
    case FETCH_META_ERROR:
      return {
        ...state,
        meta: { ...state.meta, loading: false, error: action.payload },
      };
    default:
      return state;
  }
}
